import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Download, RefreshCw, Globe, Code, FileCode, CheckCircle2, XCircle, Clock, Layers, Timer } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  api: { icon: Code, label: "API", cls: "bg-sky-100 text-sky-700" },
  xml: { icon: FileCode, label: "XML", cls: "bg-indigo-100 text-indigo-700" },
  scraping: { icon: Globe, label: "Scraping", cls: "bg-amber-100 text-amber-700" },
};

const STATUS_META = {
  done: { cls: "bg-emerald-100 text-emerald-700", label: "Concluída" },
  failed: { cls: "bg-red-100 text-red-700", label: "Falhou" },
  running: { cls: "bg-blue-100 text-blue-700", label: "Em curso" },
};

const CRON_PRESETS = [
  { value: "0", label: "Desligado" },
  { value: "1", label: "A cada hora" },
  { value: "3", label: "A cada 3h" },
  { value: "6", label: "A cada 6h" },
  { value: "12", label: "A cada 12h" },
  { value: "24", label: "Diária" },
  { value: "48", label: "A cada 2 dias" },
  { value: "168", label: "Semanal" },
];

const LIMIT_PRESETS = [
  { value: "50", label: "50 vagas" },
  { value: "100", label: "100 vagas" },
  { value: "250", label: "250 vagas" },
  { value: "500", label: "500 vagas" },
  { value: "1000", label: "1.000 vagas" },
  { value: "5000", label: "Todas (até 5.000)" },
];

function nextRunLabel(lastRun, cronHours) {
  if (!cronHours || cronHours <= 0) return null;
  const base = lastRun?.finished_at || lastRun?.started_at;
  if (!base) return "Assim que possível";
  const next = new Date(new Date(base).getTime() + cronHours * 3600 * 1000);
  const now = new Date();
  if (next <= now) return "Assim que possível";
  const diffMin = Math.round((next - now) / 60000);
  if (diffMin < 60) return `Em ~${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `Em ~${diffH}h`;
  const diffD = Math.round(diffH / 24);
  return `Em ~${diffD} dia${diffD > 1 ? "s" : ""}`;
}

export function AdminAggregatorsSection() {
  const [aggregators, setAggregators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningNow, setRunningNow] = useState({});
  const [limits, setLimits] = useState({});
  const [savingCfg, setSavingCfg] = useState({});
  const savingCfgRef = useRef({});

  const load = useCallback(async () => {
    // Não recarregar enquanto o utilizador está a alterar configurações
    // (evita sobrescrever atualização otimista)
    if (Object.values(savingCfgRef.current).some(Boolean)) return;
    try {
      const r = await axios.get(`${API}/api/admin/aggregators`, { withCredentials: true });
      setAggregators(r.data.aggregators || []);
    } catch (e) {
      toast.error("Falha a carregar agregadores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh a cada 15s
    return () => clearInterval(t);
  }, [load]);

  const trigger = async (source) => {
    const limit = Math.max(1, Math.min(5000, parseInt(limits[source] || 5000, 10) || 5000));
    setRunningNow((s) => ({ ...s, [source]: true }));
    try {
      await axios.post(`${API}/api/admin/aggregators/${source}/run?limit=${limit}`, null, { withCredentials: true });
      toast.success(`Importação de ${source} iniciada — ${limit >= 5000 ? "todas as vagas disponíveis" : limit + " vagas"}`);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Falha a iniciar ${source}`);
    } finally {
      setTimeout(() => setRunningNow((s) => ({ ...s, [source]: false })), 3000);
    }
  };

  const updateConfig = async (source, patch, successMsg) => {
    savingCfgRef.current = { ...savingCfgRef.current, [source]: true };
    setSavingCfg((s) => ({ ...s, [source]: true }));
    // otimista: refletir logo na UI
    setAggregators((prev) => prev.map((a) => (a.source === source ? { ...a, ...patch } : a)));
    try {
      await axios.patch(`${API}/api/admin/aggregators/${source}`, patch, { withCredentials: true });
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha a guardar configuração");
      // reverter chamando load explicitamente
      savingCfgRef.current = { ...savingCfgRef.current, [source]: false };
      await load();
    } finally {
      // manter marker durante 1s para o poll não sobrescrever imediatamente
      setTimeout(() => {
        savingCfgRef.current = { ...savingCfgRef.current, [source]: false };
        setSavingCfg((s) => ({ ...s, [source]: false }));
      }, 1000);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">A carregar agregadores…</div>;

  return (
    <div>
      <div className="mb-5">
        <h3 className="font-heading font-black tracking-tighter text-xl text-slate-900 flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-600" /> Agregadores multi-fonte
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Importa vagas de portais externos (APIs, XML e scraping). Cada corrida atualiza vagas existentes e adiciona novas.
          As vagas importadas são marcadas como &quot;Externa&quot; e o &quot;candidatar&quot; redireciona para o site original.
          Define um intervalo em cada fonte para importação automática — o cron corre a cada 15 min e dispara as fontes que atingiram o intervalo.
          Cada conector mostra: <b>ativas</b> (total), <b>novas 24h</b> e <b>antigas</b> (sem atualização há mais de 30 dias).
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {aggregators.map((a) => {
          const TypeIcon = (TYPE_META[a.type] || TYPE_META.api).icon;
          const typeCls = (TYPE_META[a.type] || TYPE_META.api).cls;
          const typeLabel = (TYPE_META[a.type] || TYPE_META.api).label;
          const lastRun = a.last_run;
          const statusMeta = lastRun ? STATUS_META[lastRun.status] : null;
          const isRunning = lastRun?.status === "running" || runningNow[a.source];
          const canRun = a.implemented && a.key_configured && a.enabled !== false;
          const cronVal = String(a.cron_hours ?? 0);
          const nextLabel = a.enabled !== false ? nextRunLabel(lastRun, a.cron_hours) : null;
          return (
            <div
              key={a.source}
              data-testid={`aggregator-${a.source}`}
              className={`bg-white rounded-xl border p-5 ${a.implemented ? "border-black/5" : "border-slate-200 opacity-70"} ${a.enabled === false ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold tracking-tighter text-slate-900 flex items-center gap-2 flex-wrap">
                    <span>{a.label}</span>
                    <Badge className={`${typeCls} hover:${typeCls} text-[10px] font-bold`}>
                      <TypeIcon className="w-3 h-3 mr-1" /> {typeLabel}
                    </Badge>
                    {!a.implemented && (
                      <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[10px]">Em breve</Badge>
                    )}
                    {a.enabled === false && (
                      <Badge data-testid={`aggregator-${a.source}-disabled-badge`} className="bg-slate-200 text-slate-600 hover:bg-slate-200 text-[10px]">Desativada</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-1">{a.source}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-3">
                    <div>
                      <p data-testid={`aggregator-${a.source}-total`} className="font-heading font-black tracking-tighter text-2xl text-slate-900 leading-none">{a.total_active}</p>
                      <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mt-1">ativas</p>
                    </div>
                    <div className="border-l border-slate-100 pl-3">
                      <p data-testid={`aggregator-${a.source}-new24h`} className="font-heading font-black tracking-tighter text-lg text-emerald-600 leading-none">{a.new_24h ?? 0}</p>
                      <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mt-1">novas 24h</p>
                    </div>
                    <div className="border-l border-slate-100 pl-3">
                      <p data-testid={`aggregator-${a.source}-old`} className="font-heading font-black tracking-tighter text-lg text-amber-600 leading-none">{a.old_active ?? 0}</p>
                      <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 mt-1">antigas</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs mb-3 flex-wrap">
                {a.implemented && !a.requires_key ? (
                  <span className="flex flex-col gap-0.5 text-emerald-700">
                    <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Não precisa de chave</span>
                    {a.source === "sapo" && <span className="text-slate-500">Com SCRAPERAPI_KEY chega às 5.000 — sem ela o SAPO bloqueia após ~130 vagas.</span>}
                  </span>
                ) : a.key_configured ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Chave configurada
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-red-600">
                    <XCircle className="w-3.5 h-3.5" /> Falta: <code className="font-mono">{a.requires_key}</code>
                  </span>
                )}
              </div>

              {a.implemented && (
                <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-center bg-slate-50 rounded-lg px-3 py-3 mb-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid={`aggregator-${a.source}-toggle`}
                      checked={a.enabled !== false}
                      disabled={savingCfg[a.source]}
                      onCheckedChange={(v) => updateConfig(a.source, { enabled: v }, v ? "Fonte ativada" : "Fonte desativada")}
                    />
                    <span className="text-xs font-medium text-slate-700">
                      {a.enabled !== false ? "Ativa" : "Desativada"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-end flex-wrap">
                    <Timer className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-500">Cron:</span>
                    <Select
                      value={cronVal}
                      disabled={savingCfg[a.source] || a.enabled === false}
                      onValueChange={(v) => updateConfig(a.source, { cron_hours: parseInt(v, 10) }, v === "0" ? "Cron desligado" : `Cron: ${CRON_PRESETS.find((p) => p.value === v)?.label || v + "h"}`)}
                    >
                      <SelectTrigger data-testid={`aggregator-${a.source}-cron`} className="h-8 w-[150px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CRON_PRESETS.map((p) => (
                          <SelectItem key={p.value} value={p.value} className="text-xs">
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {nextLabel && a.cron_hours > 0 && (
                      <Badge className="bg-purple-50 text-purple-700 hover:bg-purple-50 text-[10px] font-medium">
                        Próximo: {nextLabel}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {lastRun && (
                <div className="text-xs bg-slate-50 rounded-lg px-3 py-2 mb-3 flex flex-wrap items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600">{new Date(lastRun.started_at).toLocaleString("pt-PT")}</span>
                  {statusMeta && <Badge className={`${statusMeta.cls} hover:${statusMeta.cls} text-[10px]`}>{statusMeta.label}</Badge>}
                  {lastRun.trigger && (
                    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px] uppercase tracking-widest">{lastRun.trigger}</Badge>
                  )}
                  {lastRun.status === "done" && (
                    <span className="text-slate-500">
                      · {lastRun.found} encontradas · <b className="text-emerald-700">{lastRun.imported} novas</b> · {lastRun.updated} atualizadas
                      {lastRun.flagged > 0 && <> · <b className="text-amber-700">{lastRun.flagged} retidas p/ moderação IA</b></>}
                    </span>
                  )}
                  {lastRun.status === "running" && (
                    <span data-testid={`aggregator-${a.source}-progress`} className="text-amber-700">
                      · {lastRun.phase === "save" ? `a gravar ${lastRun.found || 0} vagas (${(lastRun.imported || 0) + (lastRun.updated || 0)} feitas)` : "a recolher vagas da fonte…"}
                    </span>
                  )}
                  {lastRun.status === "failed" && lastRun.error && (
                    <span className="text-red-600 w-full mt-1 truncate" title={lastRun.error}>{lastRun.error}</span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Importar:</span>
                <Select
                  value={String(limits[a.source] ?? a.last_limit ?? 5000)}
                  disabled={!canRun || isRunning}
                  onValueChange={(v) => setLimits((s) => ({ ...s, [a.source]: v }))}
                >
                  <SelectTrigger data-testid={`aggregator-${a.source}-limit`} className="h-9 w-[170px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LIMIT_PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value} className="text-sm">
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid={`aggregator-${a.source}-run`}
                  size="sm"
                  className="rounded-full bg-slate-900 hover:bg-slate-800 ml-auto"
                  disabled={!canRun || isRunning}
                  onClick={() => trigger(a.source)}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {isRunning ? "A importar…" : "Importar agora"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="aggregators-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
        </Button>
      </div>
    </div>
  );
}
